// TODO before go-live: DELETE FROM public.alerts WHERE created_at < '[set cutoff ISO date]'
//   to remove any seeded test alerts from development databases.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconShield } from '../components/layout/NavIcons';
import { Spinner } from '../components/Spinner';
import { useAuth } from '../contexts/AuthContext';
import { useUserContext } from '../hooks/useUserContext';
import { supabase } from '../lib/supabase';
import { getLockedAlertsCount, listAlerts } from '../server/api';
import { formatRelativeTime } from '../lib/timeFormat';
import { CATEGORY_OPTIONS, type CategoryKey, categoryLabel } from '../data/sampleAlerts';

export type AlertRow = {
  id: string;
  user_id: string;
  category: string;
  severity: string;
  title: string;
  description: string;
  recommendation: string;
  is_read: boolean;
  is_locked?: boolean;
  created_at: string;
};

function startOfCurrentMonth(d = new Date()): Date {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function notifyAlertsChanged() {
  window.dispatchEvent(new Event('shieldsme-alerts-changed'));
}

function parseRecommendationSteps(text: string): string[] {
  return text
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function severityStyle(sev: string): {
  bar: string;
  badge: string;
  label: string;
} {
  const s = sev.toLowerCase();
  if (s === 'high') {
    return {
      bar: 'bg-danger',
      badge: 'bg-danger/20 text-danger border-danger/30',
      label: 'High',
    };
  }
  if (s === 'medium') {
    return {
      bar: 'bg-warning',
      badge: 'bg-warning/20 text-warning border-warning/30',
      label: 'Medium',
    };
  }
  return {
    bar: 'bg-accent',
    badge: 'bg-accent/15 text-accent border-accent/30',
    label: 'Low',
  };
}

function staggerStyle(i: number): React.CSSProperties {
  return { animationDelay: `${80 + i * 60}ms` };
}

export default function AlertsPage() {
  const { currentUser } = useAuth();
  const { display } = useUserContext();
  const userId = currentUser?.id ?? null;

  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [lockedCount, setLockedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  const [selectedCategories, setSelectedCategories] = useState<CategoryKey[]>([]);
  const [severityFilter, setSeverityFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [readFilter, setReadFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [search, setSearch] = useState('');

  const monthStart = useMemo(() => startOfCurrentMonth(), []);

  const loadAlerts = useCallback(async () => {
    if (currentUser == null || !currentUser.id) {
      setAlerts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);

    const [alRes, lockedRes] = await Promise.all([listAlerts(), getLockedAlertsCount()]);

    if (!alRes.ok) {
      setLoadError(alRes.error || 'Could not load alerts.');
      setAlerts([]);
      setLockedCount(0);
    } else {
      setAlerts(
        alRes.alerts.map((a) => ({
          ...a,
          recommendation: a.recommendation ?? '',
        }))
      );
      setLockedCount(lockedRes.ok ? lockedRes.count : 0);
    }

    setLoading(false);
    notifyAlertsChanged();
  }, [currentUser]);

  useEffect(() => {
    void loadAlerts();
  }, [loadAlerts]);

  const stats = useMemo(() => {
    const totalThisMonth = alerts.filter((a) => new Date(a.created_at) >= monthStart).length;
    const highSeverityCount = alerts.filter((a) => a.severity.toLowerCase() === 'high').length;
    const unreadCount = alerts.filter((a) => !a.is_read).length;
    return { totalThisMonth, highSeverityCount, unreadCount };
  }, [alerts, monthStart]);

  const filteredAlerts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return alerts.filter((a) => {
      if (selectedCategories.length > 0 && !selectedCategories.includes(a.category as CategoryKey)) {
        return false;
      }
      if (severityFilter !== 'all' && a.severity.toLowerCase() !== severityFilter) {
        return false;
      }
      if (readFilter === 'unread' && a.is_read) return false;
      if (readFilter === 'read' && !a.is_read) return false;
      if (q) {
        const hay = a.is_locked
          ? `${a.title} ${a.category} ${a.severity}`.toLowerCase()
          : `${a.title} ${a.description} ${a.recommendation}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [alerts, selectedCategories, severityFilter, readFilter, search]);

  const hasActiveFilters =
    selectedCategories.length > 0 ||
    severityFilter !== 'all' ||
    readFilter !== 'all' ||
    search.trim().length > 0;

  const toggleCategory = (key: CategoryKey) => {
    setSelectedCategories((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      return [...prev, key];
    });
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSeverityFilter('all');
    setReadFilter('all');
    setSearch('');
  };

  const setMarkReadLocal = (id: string, isRead: boolean) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, is_read: isRead } : a)));
  };

  const markAsRead = async (id: string, isLocked?: boolean) => {
    if (!userId) return;
    if (isLocked) return;
    setMarkingId(id);
    const { error } = await supabase
      .from('alerts')
      .update({ is_read: true })
      .eq('id', id)
      .eq('user_id', userId)
      .eq('is_locked', false);
    if (!error) {
      setMarkReadLocal(id, true);
      notifyAlertsChanged();
    }
    setMarkingId(null);
  };

  const markAllRead = async () => {
    if (!userId) return;
    setMarkingAll(true);
    const { error } = await supabase
      .from('alerts')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false)
      .eq('is_locked', false);
    if (!error) {
      setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true })));
      notifyAlertsChanged();
    }
    setMarkingAll(false);
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pillBase =
    'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50';
  const pillInactive = 'border-border bg-surface/40 text-muted hover:border-border hover:bg-foreground/5 hover:text-foreground';
  const pillActive = 'border-accent/40 bg-accent/15 text-accent';

  if (!userId) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center text-muted">
        <p>Sign in to view your alerts.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-muted">
        <Spinner className="h-8 w-8 text-accent" />
        <p className="text-sm">Loading alerts…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-4xl rounded-2xl border border-border bg-surface/60 p-8 text-center">
        <p className="text-foreground">{loadError}</p>
        <button
          type="button"
          onClick={() => void loadAlerts()}
          className="mt-4 rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-semibold text-accent hover:bg-accent/20"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <header className="opacity-0 animate-fade-in-up space-y-4" style={staggerStyle(0)}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              Your Security Alerts
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted sm:text-base">
              {display.alertSubtitle}
            </p>
            {lockedCount > 0 ? (
              <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-foreground">
                <span>
                  🔒 {lockedCount} alert{lockedCount === 1 ? '' : 's'} are locked. Upgrade to view threat details.
                </span>
                <Link
                  to="/marketplace"
                  className="font-semibold text-accent underline-offset-2 hover:underline"
                >
                  Upgrade to view →
                </Link>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => void markAllRead()}
            disabled={markingAll || stats.unreadCount === 0}
            className="shrink-0 rounded-lg border border-border bg-surface/60 px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-accent/40 hover:bg-accent/10 hover:text-accent disabled:pointer-events-none disabled:opacity-40"
          >
            {markingAll ? 'Marking…' : 'Mark all read'}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-border bg-surface/60 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted">This month</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{stats.totalThisMonth}</p>
            <p className="mt-0.5 text-xs text-muted">Total alerts</p>
          </div>
          <div className="rounded-xl border border-border bg-surface/60 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-danger">High severity</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-danger">{stats.highSeverityCount}</p>
            <p className="mt-0.5 text-xs text-muted">Across all alerts</p>
          </div>
          <div className="rounded-xl border border-border bg-surface/60 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-warning">Unread</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-warning">{stats.unreadCount}</p>
            <p className="mt-0.5 text-xs text-muted">Needs attention</p>
          </div>
          <div className="flex flex-col justify-center rounded-xl border border-border bg-surface/60 p-4">
            {stats.highSeverityCount === 0 ? (
              <span className="inline-flex w-fit items-center rounded-full border border-accent/35 bg-accent/15 px-3 py-1 text-xs font-semibold text-accent">
                All clear
              </span>
            ) : (
              <span className="text-xs leading-relaxed text-muted">Resolve or review highs below</span>
            )}
          </div>
        </div>
      </header>

      <section
        className="opacity-0 animate-fade-in-up space-y-4 rounded-2xl border border-border bg-surface/50 p-4 sm:p-5"
        style={staggerStyle(1)}
      >
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">Category</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedCategories([])}
              className={`${pillBase} ${selectedCategories.length === 0 ? pillActive : pillInactive}`}
            >
              All
            </button>
            {CATEGORY_OPTIONS.map((c) => {
              const on = selectedCategories.includes(c.key);
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => toggleCategory(c.key)}
                  className={`${pillBase} ${on ? pillActive : pillInactive}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">Severity</p>
          <div className="flex flex-wrap gap-2">
            {(
              [
                { v: 'all' as const, l: 'All Severities' },
                { v: 'high' as const, l: 'High' },
                { v: 'medium' as const, l: 'Medium' },
                { v: 'low' as const, l: 'Low' },
              ] as const
            ).map(({ v, l }) => (
              <button
                key={v}
                type="button"
                onClick={() => setSeverityFilter(v)}
                className={`${pillBase} ${severityFilter === v ? pillActive : pillInactive}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">Read status</p>
          <div className="flex flex-wrap gap-2">
            {(
              [
                { v: 'all' as const, l: 'All' },
                { v: 'unread' as const, l: 'Unread' },
                { v: 'read' as const, l: 'Read' },
              ] as const
            ).map(({ v, l }) => (
              <button
                key={v}
                type="button"
                onClick={() => setReadFilter(v)}
                className={`${pillBase} ${readFilter === v ? pillActive : pillInactive}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="alert-search" className="sr-only">
            Search alerts
          </label>
          <input
            id="alert-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search alerts by keyword..."
            className="w-full rounded-xl border border-border bg-navy/60 px-4 py-3 text-sm text-foreground placeholder:text-muted focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/25"
          />
        </div>
      </section>

      <section className="space-y-4">
        {filteredAlerts.length === 0 ? (
          alerts.length === 0 ? (
            <div
              className="opacity-0 animate-fade-in-up flex flex-col items-center justify-center rounded-2xl border border-border bg-surface/40 px-6 py-16 text-center"
              style={staggerStyle(2)}
            >
              <span
                className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 sm:h-24 sm:w-24"
                aria-hidden
              >
                <IconShield className="h-12 w-12 sm:h-14 sm:w-14" />
              </span>
              <h3 className="mt-6 text-lg font-semibold text-foreground sm:text-xl">No alerts yet</h3>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-muted sm:text-base">
                ShieldSME is actively monitoring your assets. We&apos;ll notify you here the moment we detect
                anything suspicious.
              </p>
            </div>
          ) : (
            <div
              className="opacity-0 animate-fade-in-up flex flex-col items-center justify-center rounded-2xl border border-border bg-surface/40 px-6 py-16 text-center"
              style={staggerStyle(2)}
            >
              <IconShield className="h-20 w-20 text-accent sm:h-24 sm:w-24" />
              <h3 className="mt-6 text-lg font-semibold text-foreground sm:text-xl">No alerts match your filters</h3>
              <p className="mt-2 max-w-md text-sm text-muted sm:text-base">
                Try adjusting your filters or search term
              </p>
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="mt-6 rounded-lg border border-accent/40 bg-accent/10 px-5 py-2.5 text-sm font-semibold text-accent hover:bg-accent/20"
                >
                  Clear all filters
                </button>
              ) : null}
            </div>
          )
        ) : (
          <ul className="space-y-4">
            {filteredAlerts.map((alert, i) => {
              const sev = severityStyle(alert.severity);
              const expanded = expandedIds.has(alert.id);
              const steps = parseRecommendationSteps(alert.recommendation);
              const unreadCard = !alert.is_read;
              const locked = alert.is_locked === true;

              return (
                <li
                  key={alert.id}
                  className={`opacity-0 animate-fade-in-up overflow-hidden rounded-2xl border border-border ${
                    unreadCard ? 'bg-surface/90' : 'bg-surface/55'
                  }`}
                  style={staggerStyle(Math.min(i, 8))}
                >
                  <div className="flex">
                    <div className={`w-1 shrink-0 ${sev.bar}`} aria-hidden />
                    <div className="relative min-w-0 flex-1 p-4 sm:p-5">
                      {locked ? (
                        <span
                          className="absolute right-4 top-4 rounded-full border border-border bg-foreground/10 px-2 py-0.5 text-xs font-semibold text-muted"
                          aria-hidden
                        >
                          🔒
                        </span>
                      ) : null}
                      <div className="flex flex-wrap items-center gap-2 gap-y-2">
                        <span
                          className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${sev.badge}`}
                        >
                          {sev.label}
                        </span>
                        <span className="rounded-full border border-border bg-foreground/5 px-2.5 py-0.5 text-xs font-medium text-muted">
                          {categoryLabel(alert.category)}
                        </span>
                        <span className="ml-auto text-xs text-muted sm:text-sm">
                          {formatRelativeTime(alert.created_at)}
                        </span>
                      </div>
                      <h3 className="mt-3 text-base font-medium leading-snug text-foreground">{alert.title}</h3>
                      {locked ? (
                        <div className="relative mt-2 min-h-[3.5rem] overflow-hidden rounded-lg border border-border/60 bg-foreground/5">
                          <p
                            className="p-3 text-sm leading-relaxed text-muted"
                            style={{ filter: 'blur(4px)', userSelect: 'none', pointerEvents: 'none' }}
                          >
                            {alert.description}
                          </p>
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-surface/80 px-3 py-4 text-center">
                            <p className="text-xs font-medium text-muted sm:text-sm">Threat details are hidden</p>
                            <Link
                              to="/marketplace"
                              className="inline-flex rounded-lg border border-accent/40 bg-accent/15 px-4 py-2 text-sm font-semibold text-accent hover:bg-accent/25"
                            >
                              Upgrade to view →
                            </Link>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted">{alert.description}</p>
                      )}

                      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                        {!alert.is_read && !locked ? (
                          <button
                            type="button"
                            onClick={() => void markAsRead(alert.id, locked)}
                            disabled={markingId === alert.id}
                            className="rounded-lg border border-border bg-navy/50 px-3 py-2 text-sm font-semibold text-foreground hover:border-accent/40 hover:bg-accent/10 hover:text-accent disabled:opacity-50"
                          >
                            {markingId === alert.id ? 'Saving…' : 'Mark as read'}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            if (locked) return;
                            toggleExpanded(alert.id);
                          }}
                          aria-expanded={expanded}
                          disabled={locked}
                          className="rounded-lg border border-accent/35 bg-accent/10 px-3 py-2 text-sm font-semibold text-accent hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {locked ? 'Upgrade to view details' : expanded ? 'Hide steps' : 'What should I do?'}
                        </button>
                      </div>

                      {expanded && !locked ? (
                        <div
                          className="mt-4 rounded-xl border border-border bg-navy/40 p-4"
                          role="region"
                          aria-label="Recommended steps"
                        >
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted">What to do</p>
                          <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-foreground">
                            {steps.map((step, idx) => (
                              <li key={idx} className="marker:font-semibold">
                                {step.replace(/^\d+\.\s*/, '')}
                              </li>
                            ))}
                          </ol>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
